import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { FundType } from '../../../../../models/transactions/fund-type';
import { WithdrawalInstruction } from '../../../../../models/submissions/withdrawal-instruction';
import { FundTypeId } from '../../../../../models/enum/shared/fund-type-id';
import { RecurringPaymentAmountTypeEnum } from '../../../../../models/enum/shared/recurring-payment-amount-type.enum';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { RecurringPaymentInstruction } from '../../../../../models/submissions/recurring-payment-instruction';
import { LumpSumPaymentInstruction } from '../../../../../models/transactions/lump-sum-payment-instruction';
import { AnnualMinimumDetails } from '../../../../../models/transactions/annual-minimum-details';
import { totalPercentageMustEqualValidator } from '../../../../../validators/total-percentage-must-equal-validator';
import {
  VbWithdrawalInstructionsFormGroup,
  VbWithdrawalStepFormGroup
} from '../../../../../models/shared/forms/vb-withdrawal-step-form-group';
import { VbPaymentChangeDepositsStepFormGroup } from '../../../../../models/shared/forms/vb-payment-change-deposits-step-form-group';
import { VbPaymentChangeMinAgeFormGroup } from '../../../../../models/shared/forms/vb-payment-change-min-age-form-group';
import { VbPaymentChangePaymentStepFormGroup } from '../../../../../models/shared/forms/vb-payment-change-payment-step-form-group';
import { VbPaymentChangeRecurringPaymentFormGroup } from '../../../../../models/shared/forms/vb-payment-change-recurring-payment-form-group';
import { lumpSumPaymentChangeStartDateValidator } from './vb-payment-change-lump-sum-start-date.validator';
import { Spouse } from '../../../../../models/users/dto/spouse';
import { VbMinimumAgeUtils } from '../../../../../utils/vb-minimum-age-utils';

export class VbPaymentChangeFormFactory {
  public static buildMinAgeForm(
    currentYearAnnualMinimumDetails: AnnualMinimumDetails,
    memberBirthDate: string,
    activeSpouse: Spouse | null
  ): VbPaymentChangeMinAgeFormGroup {
    const currentSpouseId = currentYearAnnualMinimumDetails.minimumAgeSpouseId ?? null;
    const minimumAgeDateOfBirth = VbMinimumAgeUtils.resolveMinimumAgeDateOfBirth(
      currentSpouseId,
      memberBirthDate,
      activeSpouse
    );
    return new FormGroup({
      minimumAgeSpouseId: new FormControl<number | null>(minimumAgeDateOfBirth === null ? null : currentSpouseId),
      minimumAgeDateOfBirth: new FormControl<string | null>(minimumAgeDateOfBirth, {
        validators: [Validators.required]
      })
    });
  }

  public static buildPaymentStepForm(
    scheduledLumpSum: LumpSumPaymentInstruction | null,
    allowableChangeDate: string | null,
    forgoRecurringPayments: boolean,
    totalBalance: number
  ): VbPaymentChangePaymentStepFormGroup {
    const hasLumpSum = !!scheduledLumpSum;
    const form = new FormGroup({
      forgoRecurringPayments: new FormControl<boolean>(forgoRecurringPayments, {
        nonNullable: true
      }),
      lumpSumPaymentAmount: new FormControl<number | null>(
        { value: scheduledLumpSum?.paymentAmount ?? null, disabled: !hasLumpSum && !forgoRecurringPayments },
        { validators: [Validators.required, Validators.min(0.01), Validators.max(totalBalance)] }
      ),
      lumpSumPaymentMonth: new FormControl<number | null>(
        {
          value: scheduledLumpSum?.desiredPaymentDate
            ? moment.utc(scheduledLumpSum.desiredPaymentDate).month() + 1
            : null,
          disabled: !hasLumpSum && !forgoRecurringPayments
        },
        { validators: [Validators.required] }
      ),
      lumpSumPaymentYear: new FormControl<number | null>(
        {
          value: scheduledLumpSum?.desiredPaymentDate ? moment.utc(scheduledLumpSum.desiredPaymentDate).year() : null,
          disabled: !hasLumpSum && !forgoRecurringPayments
        },
        { validators: [Validators.required] }
      )
    });

    if (allowableChangeDate) {
      form.addValidators(lumpSumPaymentChangeStartDateValidator(allowableChangeDate));
    }

    return form;
  }

  public static buildRecurringPaymentFormGroup(
    paymentFrequencyId: VariableBenefitPaymentFrequencyEnum,
    existingInstruction: RecurringPaymentInstruction | null = null
  ): VbPaymentChangeRecurringPaymentFormGroup {
    return new FormGroup({
      paymentFrequencyId: new FormControl<number>(paymentFrequencyId, { nonNullable: true }),
      paymentStartMonth: new FormControl<number | null>(
        existingInstruction?.paymentStartDate ? moment.utc(existingInstruction.paymentStartDate).month() + 1 : null,
        { validators: [Validators.required] }
      ),
      paymentStartYear: new FormControl<number | null>(
        existingInstruction?.paymentStartDate ? moment.utc(existingInstruction.paymentStartDate).year() : null,
        { validators: [Validators.required] }
      ),
      recurringPaymentAmountType: new FormControl<RecurringPaymentAmountTypeEnum>(
        existingInstruction?.useMinimumAmount
          ? RecurringPaymentAmountTypeEnum.Minimum
          : RecurringPaymentAmountTypeEnum.Custom,
        { nonNullable: true }
      ),
      paymentAmount: new FormControl<number | null>(existingInstruction?.paymentAmount ?? null, {
        validators: [Validators.required, Validators.min(0)]
      })
    });
  }

  public static buildWithdrawalStepForm(
    fundTypes: FundType[],
    instructions: WithdrawalInstruction[]
  ): VbWithdrawalStepFormGroup {
    const instructionGroups: VbWithdrawalInstructionsFormGroup[] = [];

    // Filtering out Cash
    fundTypes
      .filter(fund => fund.id !== FundTypeId.Cash)
      .forEach(fund => {
        const instruction = instructions.find(i => i.fundId === fund.id);

        // A blank percentage represents a 0% allocation.
        const percentageControl = new FormControl<number | null>(instruction?.percentage ?? null, [
          Validators.min(0),
          Validators.max(100)
        ]);

        const balanceControl = new FormControl<number>(instruction?.fundBalance ?? 0, { nonNullable: true });

        instructionGroups.push(
          new FormGroup({
            fund: new FormControl<number>(fund.id, { nonNullable: true }),
            percentage: percentageControl,
            balance: balanceControl
          })
        );
      });

    // With a single fund there is nothing to split, so default it to 100% and save the
    // user from typing. Only do so when the field is empty and editable.
    if (instructionGroups.length === 1) {
      const onlyPercentage = instructionGroups[0].controls.percentage;
      if (onlyPercentage.enabled && onlyPercentage.value === null) {
        onlyPercentage.setValue(100);
      }
    }

    return new FormGroup({
      instructions: new FormArray<VbWithdrawalInstructionsFormGroup>(instructionGroups, [
        totalPercentageMustEqualValidator(100)
      ])
    });
  }

  public static buildForgoRecurringPaymentFormGroup(
    paymentFrequencyId: VariableBenefitPaymentFrequencyEnum,
    startMonth: number,
    startYear: number
  ): VbPaymentChangeRecurringPaymentFormGroup {
    return new FormGroup({
      paymentFrequencyId: new FormControl<number>(paymentFrequencyId, { nonNullable: true }),
      paymentStartMonth: new FormControl<number | null>(startMonth, { validators: [Validators.required] }),
      paymentStartYear: new FormControl<number | null>(startYear, { validators: [Validators.required] }),
      recurringPaymentAmountType: new FormControl<RecurringPaymentAmountTypeEnum>(
        RecurringPaymentAmountTypeEnum.Custom,
        { nonNullable: true }
      ),
      paymentAmount: new FormControl<number | null>(0, {
        validators: [Validators.required, Validators.min(0)]
      })
    });
  }

  public static buildDepositsStepForm(selectedBankAccountId: number | null): VbPaymentChangeDepositsStepFormGroup {
    return new FormGroup({
      bankAccountId: new FormControl<number | null>(selectedBankAccountId, {
        validators: [Validators.required]
      })
    });
  }
}
